import { InlineMath, BlockMath } from "react-katex";

type LatexTextProps = {
  text: string;
  className?: string;
};

type Part = { type: "text" | "inline" | "block" | "code" | "inline-code" | "table"; content: string; language?: string; tableData?: { headers: string[]; rows: string[][] } };

/**
 * Component that renders text with LaTeX math expressions and code blocks
 * Supports inline math: $...$ and \(...\)
 * Supports display math: $$...$$ and \[...\]
 * Supports code blocks: ```language...```
 * Supports inline code: `...`
 */
export function LatexText({ text, className }: LatexTextProps) {
  if (!text) return null;

  // First, extract code blocks to prevent interference with LaTeX parsing
  // Code blocks need ALL \n converted to newlines (no LaTeX inside code)
  const parts: Part[] = [];
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  
  let lastIndex = 0;
  let match;
  
  while ((match = codeBlockRegex.exec(text)) !== null) {
    // Add text before the code block (will be parsed for LaTeX later)
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index);
      // For non-code text, convert \n to newlines but preserve LaTeX commands like \neq, \nu
      // by only converting \n NOT followed by letters (excluding common word starts after newline)
      const processedTextBefore = convertNewlines(textBefore, false);
      parseLatexParts(processedTextBefore, parts);
    }
    
    // Add the code block - convert ALL \n to newlines (no LaTeX in code)
    const codeContent = convertNewlines(match[2].trim(), true);
    parts.push({
      type: "code",
      content: codeContent,
      language: match[1] || "text",
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last code block
  if (lastIndex < text.length) {
    const processedRemaining = convertNewlines(text.substring(lastIndex), false);
    parseLatexParts(processedRemaining, parts);
  }
  
  // If no code blocks found, parse the entire text for LaTeX
  if (parts.length === 0) {
    const processedText = convertNewlines(text, false);
    parseLatexParts(processedText, parts);
  }

  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part.type === "code") {
          return (
            <pre
              key={index}
              className="my-3 p-4 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre"
            >
              <code>{part.content}</code>
            </pre>
          );
        }
        if (part.type === "inline-code") {
          return (
            <code
              key={index}
              className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded text-sm font-mono"
            >
              {part.content}
            </code>
          );
        }
        if (part.type === "table" && part.tableData) {
          return (
            <div key={index} className="my-3 overflow-x-auto">
              <table className="min-w-full border-collapse border border-slate-300 dark:border-slate-600">
                <thead>
                  <tr className="bg-slate-100 dark:bg-slate-800">
                    {part.tableData.headers.map((header, i) => (
                      <th key={i} className="border border-slate-300 dark:border-slate-600 px-3 py-2 text-left text-sm font-semibold">
                        <LatexText text={header.trim()} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {part.tableData.rows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800/50"}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm">
                          <LatexText text={cell.trim()} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (part.type === "block") {
          return (
            <span key={index} className="block my-2">
              <BlockMath math={part.content} />
            </span>
          );
        }
        if (part.type === "inline") {
          return <InlineMath key={index} math={part.content} />;
        }
        // For text parts, preserve newlines
        return part.content.split("\n").map((line, lineIndex, array) => (
          <span key={`${index}-${lineIndex}`}>
            {line}
            {lineIndex < array.length - 1 && <br />}
          </span>
        ));
      })}
    </span>
  );
}

/**
 * Helper function to convert literal \n to actual newlines
 * @param text - The text to process
 * @param isCode - If true, convert ALL \n to newlines (no LaTeX in code)
 *                 If false, only convert \n that's NOT part of a LaTeX command
 */
function convertNewlines(text: string, isCode: boolean): string {
  if (isCode) {
    // In code blocks, convert ALL \n to actual newlines
    // Also handle \\n (double-escaped newlines from JSON parsing issues)
    return text.replace(/\\\\n/g, "\n").replace(/\\n/g, "\n");
  }

  // For non-code text, we need to preserve LaTeX commands starting with \n
  // Common LaTeX commands: \neq, \neg, \nu, \nabla, \not, \ni, \newline, etc.
  // Strategy: Only convert \n when followed by non-letter OR when followed by
  // a letter that doesn't form a known LaTeX command

  // List of LaTeX command prefixes after \n that should be preserved
  const latexPrefixes = [
    "eq", // \neq
    "ew", // \newline, \newcommand
    "eg", // \neg
    "u", // \nu
    "abla", // \nabla
    "ot", // \not
    "i", // \ni
    "subseteq", // \nsubseteq
    "supseteq", // \nsupseteq
    "leq", // \nleq
    "geq", // \ngeq
    "less", // \nless
    "gtr", // \ngtr
    "mid", // \nmid
    "parallel", // \nparallel
    "cong", // \ncong
    "sim", // \nsim
    "prec", // \nprec
    "succ", // \nsucc
    "vdash", // \nvdash
    "vDash", // \nvDash
    "Vdash", // \nVdash
    "VDash", // \nVDash
    "leftarrow", // \nleftarrow
    "rightarrow", // \nrightarrow
    "leftrightarrow", // \nleftrightarrow
    "atural", // \natural
    "oindent", // \noindent
    "onumber", // \nonumber
    "ormalsize", // \normalsize
    "ewpage", // \newpage
    "othing", // \nothing
    "ight", // \night - unlikely but possible in text
  ];

  // Create a regex that matches \n followed by any of the latex prefixes
  const latexPattern = new RegExp(`\\\\n(${latexPrefixes.join("|")})`, "g");

  // Step 1: Protect LaTeX commands with placeholders
  let result = text;
  const placeholders: Array<{ placeholder: string; original: string }> = [];
  let placeholderIndex = 0;

  result = result.replace(latexPattern, (match) => {
    const placeholder = `___NL_PLACEHOLDER_${placeholderIndex++}___`;
    placeholders.push({ placeholder, original: match });
    return placeholder;
  });

  // Step 2: Convert remaining \n to actual newlines
  // Also handle \\n (double-escaped newlines from JSON parsing issues)
  result = result.replace(/\\\\n/g, "\n");
  result = result.replace(/\\n/g, "\n");

  // Step 3: Restore LaTeX commands
  for (const { placeholder, original } of placeholders) {
    result = result.replace(placeholder, original);
  }

  return result;
}

/**
 * Helper function to detect and parse Markdown tables
 * A table is detected when:
 * 1. Lines contain pipe characters |
 * 2. There's a separator line with dashes (|---|---|)
 */
function parseMarkdownTable(text: string): { before: string; table: Part | null; after: string } {
  const lines = text.split("\n");
  
  // Find the start of a table (a line with | that's followed by a separator line)
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i];
    const nextLine = lines[i + 1];
    
    // Check if this could be a header line (contains |)
    if (!line.includes("|")) continue;
    
    // Check if next line is a separator line (contains |---| pattern)
    if (!nextLine.match(/^\|?[\s-:]+\|[\s-:|]+\|?$/)) continue;
    
    // Found a table! Parse it
    const tableStartIndex = i;
    let tableEndIndex = i + 1;
    
    // Find the end of the table (until a line without proper pipe structure)
    for (let j = i + 2; j < lines.length; j++) {
      const tableLine = lines[j];
      // Check if this line is part of the table (has | characters and content)
      if (tableLine.includes("|") && tableLine.trim()) {
        tableEndIndex = j;
      } else {
        break;
      }
    }
    
    // Parse the table
    const headerLine = lines[tableStartIndex];
    const headers = headerLine.split("|").filter(cell => cell.trim() !== "" || headerLine.startsWith("|"));
    // Clean up headers - remove empty first/last if line starts/ends with |
    const cleanHeaders = headerLine.startsWith("|") 
      ? headers.slice(headerLine.startsWith("|") ? 1 : 0, headerLine.endsWith("|") ? -1 : undefined)
        .map(h => h.trim())
      : headers.map(h => h.trim());
    
    const rows: string[][] = [];
    for (let j = tableStartIndex + 2; j <= tableEndIndex; j++) {
      const rowLine = lines[j];
      const cells = rowLine.split("|");
      const cleanCells = rowLine.startsWith("|")
        ? cells.slice(1, rowLine.endsWith("|") ? -1 : undefined).map(c => c.trim())
        : cells.map(c => c.trim());
      if (cleanCells.some(c => c.length > 0)) {
        rows.push(cleanCells);
      }
    }
    
    const before = lines.slice(0, tableStartIndex).join("\n");
    const after = lines.slice(tableEndIndex + 1).join("\n");
    
    return {
      before,
      table: {
        type: "table",
        content: "",
        tableData: { headers: cleanHeaders, rows }
      },
      after
    };
  }
  
  return { before: text, table: null, after: "" };
}

/**
 * Helper function to parse text for inline code and then LaTeX expressions
 */
function parseLatexParts(text: string, parts: Part[]): void {
  // First, check for Markdown tables
  const tableResult = parseMarkdownTable(text);
  
  if (tableResult.table) {
    // Parse content before the table
    if (tableResult.before.trim()) {
      parseLatexPartsNoTable(tableResult.before, parts);
    }
    
    // Add the table
    parts.push(tableResult.table);
    
    // Parse content after the table (recursively check for more tables)
    if (tableResult.after.trim()) {
      parseLatexParts(tableResult.after, parts);
    }
    return;
  }
  
  // No table found, continue with normal parsing
  parseLatexPartsNoTable(text, parts);
}

/**
 * Helper function to parse text for inline code and then LaTeX expressions (no table detection)
 */
function parseLatexPartsNoTable(text: string, parts: Part[]): void {
  // First, handle inline code (single backticks) since they're more definitive
  // This prevents backticks inside code from being misinterpreted
  const inlineCodeRegex = /`([^`]+)`/g;
  let lastIndex = 0;
  let match;
  
  const segments: Array<{ type: "text" | "inline-code"; content: string }> = [];
  
  while ((match = inlineCodeRegex.exec(text)) !== null) {
    // Add text before the inline code
    if (match.index > lastIndex) {
      segments.push({ type: "text", content: text.substring(lastIndex, match.index) });
    }
    // Add the inline code
    segments.push({ type: "inline-code", content: match[1] });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.substring(lastIndex) });
  }
  
  // If no inline code found, just process the whole text for LaTeX
  if (segments.length === 0) {
    segments.push({ type: "text", content: text });
  }
  
  // Now process each segment - inline-code goes directly to parts, text gets LaTeX parsing
  for (const segment of segments) {
    if (segment.type === "inline-code") {
      parts.push({ type: "inline-code", content: segment.content });
    } else {
      parseLatexInText(segment.content, parts);
    }
  }
}

/**
 * Helper function to parse LaTeX expressions in text (without inline code)
 */
function parseLatexInText(text: string, parts: Part[]): void {
  const remaining = text;
  let position = 0;

  while (position < remaining.length) {
    // Check for display math \[...\] (must be checked before $$)
    const blockBracketStart = remaining.indexOf("\\[", position);
    if (blockBracketStart !== -1) {
      // Add text before the block math
      if (blockBracketStart > position) {
        const textBefore = remaining.substring(position, blockBracketStart);
        if (textBefore) {
          parts.push({
            type: "text",
            content: textBefore,
          });
        }
      }

      // Find the closing \]
      const blockBracketEnd = remaining.indexOf("\\]", blockBracketStart + 2);
      if (blockBracketEnd !== -1) {
        const mathContent = remaining.substring(blockBracketStart + 2, blockBracketEnd);
        parts.push({
          type: "block",
          content: mathContent,
        });
        position = blockBracketEnd + 2;
        continue;
      }
    }

    // Check for display math $$...$$
    const blockStart = remaining.indexOf("$$", position);
    if (blockStart !== -1 && (blockBracketStart === -1 || blockStart < blockBracketStart)) {
      // Add text before the block math
      if (blockStart > position) {
        const textBefore = remaining.substring(position, blockStart);
        if (textBefore) {
          parts.push({
            type: "text",
            content: textBefore,
          });
        }
      }

      // Find the closing $$
      const blockEnd = remaining.indexOf("$$", blockStart + 2);
      if (blockEnd !== -1) {
        const mathContent = remaining.substring(blockStart + 2, blockEnd);
        // Check if this is actually inline (single variable or short expression)
        // If the content is short and doesn't contain newlines, treat as inline
        if (mathContent.length <= 30 && !mathContent.includes("\\\\") && !mathContent.includes("\\begin")) {
          parts.push({
            type: "inline",
            content: mathContent,
          });
        } else {
          parts.push({
            type: "block",
            content: mathContent,
          });
        }
        position = blockEnd + 2;
        continue;
      }
    }

    // Check for inline math \(...\)
    const inlineParenStart = remaining.indexOf("\\(", position);
    if (inlineParenStart !== -1 && (blockStart === -1 || inlineParenStart < blockStart) && (blockBracketStart === -1 || inlineParenStart < blockBracketStart)) {
      // Add text before the inline math
      if (inlineParenStart > position) {
        const textBefore = remaining.substring(position, inlineParenStart);
        if (textBefore) {
          parts.push({
            type: "text",
            content: textBefore,
          });
        }
      }

      // Find the closing \)
      const inlineParenEnd = remaining.indexOf("\\)", inlineParenStart + 2);
      if (inlineParenEnd !== -1) {
        const mathContent = remaining.substring(inlineParenStart + 2, inlineParenEnd);
        parts.push({
          type: "inline",
          content: mathContent,
        });
        position = inlineParenEnd + 2;
        continue;
      }
    }

    // Check for inline math $...$
    const inlineStart = remaining.indexOf("$", position);
    if (inlineStart !== -1 && (blockStart === -1 || inlineStart < blockStart) && (blockBracketStart === -1 || inlineStart < blockBracketStart) && (inlineParenStart === -1 || inlineStart < inlineParenStart)) {
      // Add text before the inline math
      if (inlineStart > position) {
        const textBefore = remaining.substring(position, inlineStart);
        if (textBefore) {
          parts.push({
            type: "text",
            content: textBefore,
          });
        }
      }

      // Find the closing $
      const inlineEnd = remaining.indexOf("$", inlineStart + 1);
      if (inlineEnd !== -1) {
        const mathContent = remaining.substring(inlineStart + 1, inlineEnd);
        parts.push({
          type: "inline",
          content: mathContent,
        });
        position = inlineEnd + 1;
        continue;
      }
    }

    // No more math found, add the rest as text
    const finalText = remaining.substring(position);
    if (finalText) {
      parts.push({
        type: "text",
        content: finalText,
      });
    }
    break;
  }
}
